import { Editor } from "@tiptap/core"
import { Bold } from "@tiptap/extension-bold"
import { Code } from "@tiptap/extension-code"
import { Highlight } from "@tiptap/extension-highlight"
import { Italic } from "@tiptap/extension-italic"
import { Strike } from "@tiptap/extension-strike"
import { Subscript } from "@tiptap/extension-subscript"
import { Superscript } from "@tiptap/extension-superscript"
import { Color, FontSize, LineHeight, TextStyle } from "@tiptap/extension-text-style"
import { Underline } from "@tiptap/extension-underline"
import { useEditorState } from "@tiptap/react"

import { useMarkSchemaWarning } from "./editor"

export const useMarkState = (markName: string, editor: Editor) => {
	useMarkSchemaWarning(markName, editor)
	return useEditorState({
		editor,
		selector: () => {
			return {
				isActive: editor.isActive(markName),
				canToggle: editor.can().toggleMark(markName),
				canSet: editor.can().setMark(markName),
				canUnset: editor.can().unsetMark(markName),
			}
		},
	})
}

export const useBoldState = (editor: Editor) => {
	return useMarkState(Bold.name, editor)
}

export const useCodeState = (editor: Editor) => {
	return useMarkState(Code.name, editor)
}

export const useHighlightState = (editor: Editor) => {
	useMarkSchemaWarning(Highlight.name, editor)
	return useEditorState({
		editor,
		selector: () => {
			return {
				color: (editor?.getAttributes(Highlight.name).color ?? undefined) as string | undefined,
				isActive: (color: string) => editor.isActive(Highlight.name, { color }),
				canToggle: (color: string) => editor.can().toggleHighlight({ color }),
				canSet: (color: string) => editor.can().setHighlight({ color }),
				canUnset: editor.can().unsetHighlight(),
			}
		},
	})
}

export const useItalicState = (editor: Editor) => {
	return useMarkState(Italic.name, editor)
}

export const useStrikeState = (editor: Editor) => {
	return useMarkState(Strike.name, editor)
}

export const useSubscriptState = (editor: Editor) => {
	return useMarkState(Subscript.name, editor)
}

export const useSuperscriptState = (editor: Editor) => {
	return useMarkState(Superscript.name, editor)
}

export const useUnderlineState = (editor: Editor) => {
	return useMarkState(Underline.name, editor)
}

export const useTextStyleState = (editor: Editor) => {
	useMarkSchemaWarning(TextStyle.name, editor)
	return useEditorState({
		editor,
		selector: () => {
			return {
				color: (editor.getAttributes(TextStyle.name)[Color.name] ?? undefined) as string | undefined,
				fontSize: (editor.getAttributes(TextStyle.name)[FontSize.name] ?? undefined) as string | undefined,
				lineHeight: (editor.getAttributes(TextStyle.name)[LineHeight.name] ?? undefined) as string | undefined,
			}
		},
	})
}
